import type { Camera } from "three";

import { DRAG_THRESHOLD_PIXELS } from "../renderConstants";
import { screenPointToRay } from "../utilities/screenPointToRay";
import {
  type PickableWorldObject,
  isPickableWorldObject
} from "../world/PickableWorldObject";
import type { World } from "../world/World";
import { raycastWorldObjects } from "../world/utilities/raycastWorldObjects";

export class WorldObjectPicker {
  private pressPosition: { clientX: number; clientY: number } | null = null;
  private hoverPosition: { clientX: number; clientY: number } | null = null;
  private pickedWorldObject: PickableWorldObject | null = null;
  private hoveringWorldObject: PickableWorldObject | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: Camera,
    private readonly world: World
  ) {
    canvas.addEventListener("pointerdown", this.onPointerDown, false);
    canvas.addEventListener("pointerup", this.onPointerUp, false);
    canvas.addEventListener("pointermove", this.onPointerMove);
  }

  dispose() {
    this.unpick();
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
  }

  private readonly onPointerDown = (event: PointerEvent) => {
    if (!event.isPrimary || event.button !== 0) return;
    this.pressPosition = { clientX: event.clientX, clientY: event.clientY };
  };

  private readonly onPointerUp = (event: PointerEvent) => {
    if (!this.pressPosition) return;
    const pressPosition = this.pressPosition;
    this.pressPosition = null;

    const distanceMoved = Math.hypot(
      event.clientX - pressPosition.clientX,
      event.clientY - pressPosition.clientY
    );
    if (distanceMoved > DRAG_THRESHOLD_PIXELS) return;

    this.pickAt(event.clientX, event.clientY);
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    if (this.hoverPosition) {
      const hoverPosition = this.hoverPosition;
      const distanceMoved = Math.hypot(
        event.clientX - hoverPosition.clientX,
        event.clientY - hoverPosition.clientY
      );
      if (distanceMoved < DRAG_THRESHOLD_PIXELS) return;
    }

    const { clientX, clientY } = event;
    this.hoverPosition = { clientX, clientY };

    const ray = screenPointToRay(this.camera, this.canvas, clientX, clientY);
    const pickableWorldObjects = [...this.world.getObjects()].filter(
      isPickableWorldObject
    );
    // This is computationally expensive... we can fix it later though.
    const hit = raycastWorldObjects(ray, pickableWorldObjects);
    if (hit?.worldObject === this.hoveringWorldObject) return;

    this.hoveringWorldObject?.onUnhover?.();
    this.hoveringWorldObject = hit?.worldObject ?? null;
    hit?.worldObject?.onHover?.(hit.point);
  };

  private pickAt(clientX: number, clientY: number) {
    const ray = screenPointToRay(this.camera, this.canvas, clientX, clientY);
    const pickableWorldObjects = [...this.world.getObjects()].filter(
      isPickableWorldObject
    );
    // This is computationally expensive... we can fix it later though.
    const hit = raycastWorldObjects(ray, pickableWorldObjects);
    if (hit?.worldObject !== this.pickedWorldObject) this.unpick();
    if (!hit) return;

    hit.worldObject.onPicked(hit.point);
    this.pickedWorldObject = hit.worldObject;
  }

  private unpick() {
    const picked = this.pickedWorldObject;
    this.pickedWorldObject = null;
    if (picked && this.world.getObjects().has(picked)) picked.onUnpicked?.();
  }
}
